import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import HeroBanner from "../components/HeroBanner";
import UpcomingRow from "../components/UpcomingRow";
import MovieRow from "../components/MovieRow";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Home() {
  const { user } = useAuth();
  const [movies, setMovies] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/movies");
      setMovies(data);
    } catch (err) {
      console.error("load movies failed", err);
    } finally {
      setLoading(false);
    }
  };

  const loadFavIds = async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get("/favorites/ids");
      setFavIds(data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    loadFavIds();
  }, [user]);

  const toggleFav = async (id) => {
    if (!user || user === false) {
      toast.error("Connectez-vous pour utiliser Ma Liste");
      return;
    }
    const inList = favIds.includes(id);
    try {
      if (inList) {
        await api.delete(`/favorites/${id}`);
        setFavIds(favIds.filter((x) => x !== id));
        toast.success("Retiré de Ma Liste");
      } else {
        await api.post(`/favorites/${id}`);
        setFavIds([...favIds, id]);
        toast.success("Ajouté à Ma Liste");
      }
    } catch (err) {
      console.error("load favorite ids failed", err);
    }
  };

  const hero = movies[0];
  const trending = movies.slice(0, 10);
  const by4k = movies.filter((m) => m.is_4k);
  const byGenre = useMemo(() => {
    const map = {};
    for (const m of movies) {
      if (!m.genre) continue;
      map[m.genre] = map[m.genre] || [];
      map[m.genre].push(m);
    }
    return map;
  }, [movies]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 font-mono-mini">
        Chargement du catalogue…
      </div>
    );
  }

  if (movies.length === 0) {
    return (
      <div className="space-y-16 pt-24 pb-24 max-w-[1600px] mx-auto">
        <UpcomingRow />
        <div className="px-6 flex flex-col items-center justify-center text-center space-y-6">
          <div className="font-mono-mini text-[11px] uppercase tracking-[0.4em] text-[color:var(--ns-accent)]">
            Catalogue en préparation
          </div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase">Le cinéma arrive bientôt</h2>
          <p className="max-w-xl text-zinc-400">
            Aucun film n&apos;a encore été publié. Connectez-vous en tant qu&apos;administrateur
            pour uploader vos premiers films en 4K.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-16 pb-24">
      <HeroBanner movie={hero} />
      <UpcomingRow />

      <MovieRow
        label="À la Une"
        title="Tendances cette semaine"
        movies={trending}
        favoriteIds={favIds}
        onToggleList={toggleFav}
      />
      {by4k.length > 0 && (
        <MovieRow
          label="Édition 4K"
          title="Masterisé en Ultra HD"
          movies={by4k}
          favoriteIds={favIds}
          onToggleList={toggleFav}
          variant="poster"
        />
      )}
      {Object.entries(byGenre).map(([g, items]) =>
        items.length > 0 ? (
          <MovieRow
            key={g}
            label={g}
            title={`Collection ${g}`}
            movies={items}
            favoriteIds={favIds}
            onToggleList={toggleFav}
          />
        ) : null
      )}
    </div>
  );
}
